import { useMemo } from 'react';
import { Chip, Group, Stack, Text } from '@mantine/core';
import { FundDistribution } from '../../types';
import { CATALOGUE_COPY, CatalogueLanguage } from './catalogue_copy';
import { CatalogueFilters, CatalogueFund, FacetKey, countFacetMatches, formatNumber } from './catalogue_helpers';

/**
 * Pill colours give each facet its own colour language so an RM can tell at a glance which
 * kind of filter is active: open ended is green, closed ended indigo, availability teal,
 * asset classes gray. The fund cards themselves (`catalogue_cards.tsx`) use neutral olive
 * chips by design, so the pills are the only place these colours appear.
 */
const TYPE_COLORS: Record<string, string> = { open_ended: 'green', closed_ended: 'indigo' };
const ACTIVITY_COLORS: Record<string, string> = { active: 'teal', inactive: 'orange' };
const CURRENCY_COLOR = 'blue';
const ASSET_CLASS_COLOR = 'gray';

interface PillOption {
    value: string;
    label: string;
    color: string;
}

interface PillRowProps {
    title: string;
    allLabel: string;
    options: PillOption[];
    /** null = the "all" pill is selected. */
    selected: string | null;
    allCount: number;
    counts: Record<string, number>;
    language: CatalogueLanguage;
    onChange: (value: string | null) => void;
}

/**
 * One facet as a row of single-select pills.
 *
 * Plain `Chip`s rather than a `Chip.Group`: the group's single-select mode has radio
 * semantics, so the selected pill could not be clicked again to clear it. Here a second
 * click on the selected pill goes back to "all", which is the gesture people expect.
 */
const PillRow = ({ title, allLabel, options, selected, allCount, counts, language, onChange }: PillRowProps) => (
    <Group gap="xs" align="center" wrap="wrap" role="group" aria-label={title}>
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" miw={88}>{title}</Text>
        <Chip
            size="sm"
            variant="light"
            color="dark"
            checked={selected === null}
            onChange={() => onChange(null)}
        >
            {allLabel} · {formatNumber(allCount, language)}
        </Chip>
        {options.map((option) => {
            const count = counts[option.value] ?? 0;
            const isSelected = selected === option.value;
            return (
                <Chip
                    key={option.value}
                    size="sm"
                    color={option.color}
                    variant={isSelected ? 'filled' : 'light'}
                    checked={isSelected}
                    // A pill nobody can reach is shown but inert, so the RM learns the
                    // combination is empty without losing the rest of the row.
                    disabled={!isSelected && count === 0}
                    onChange={() => onChange(isSelected ? null : option.value)}
                >
                    {option.label} · {formatNumber(count, language)}
                </Chip>
            );
        })}
    </Group>
);

interface CatalogueFilterPillsProps {
    funds: CatalogueFund[];
    filters: CatalogueFilters;
    facets: { currencies: string[]; assetClasses: string[] };
    language: CatalogueLanguage;
    distributions?: Record<string, FundDistribution[]>;
    onChange: (key: FacetKey, value: string | null) => void;
}

/**
 * Quick filters for the fund grid: type, availability, currency and asset class.
 *
 * Writes to the same `CatalogueFilters` the search box and sort use, so the pills, the
 * result count and the "clear filters" button all agree. Each pill carries the number of
 * funds it would leave on screen given the other selected pills.
 */
export const CatalogueFilterPills = ({ funds, filters, facets, language, distributions, onChange }: CatalogueFilterPillsProps) => {
    const copy = CATALOGUE_COPY[language];

    const typeOptions = useMemo<PillOption[]>(() => [
        { value: 'open_ended', label: copy.openEnded, color: TYPE_COLORS.open_ended },
        { value: 'closed_ended', label: copy.closedEnded, color: TYPE_COLORS.closed_ended },
    ], [copy]);
    const activityOptions = useMemo<PillOption[]>(() => [
        // The row is titled "Available for orders", so the pills answer it: Yes / No.
        { value: 'active', label: copy.yes, color: ACTIVITY_COLORS.active },
        { value: 'inactive', label: copy.no, color: ACTIVITY_COLORS.inactive },
    ], [copy]);
    const currencyOptions = useMemo<PillOption[]>(
        () => facets.currencies.map((currency) => ({ value: currency, label: currency, color: CURRENCY_COLOR })),
        [facets.currencies],
    );
    const assetClassOptions = useMemo<PillOption[]>(
        () => facets.assetClasses.map((assetClass) => ({ value: assetClass, label: assetClass, color: ASSET_CLASS_COLOR })),
        [facets.assetClasses],
    );

    const rows: { key: FacetKey; title: string; allLabel: string; options: PillOption[] }[] = [
        { key: 'fundType', title: copy.type, allLabel: copy.allTypes, options: typeOptions },
        { key: 'activity', title: copy.availability, allLabel: copy.allProducts, options: activityOptions },
        { key: 'currency', title: copy.currency, allLabel: copy.allCurrencies, options: currencyOptions },
        { key: 'assetClass', title: copy.assetClass, allLabel: copy.allAssetClasses, options: assetClassOptions },
    ];

    const counts = useMemo(
        () => Object.fromEntries(rows.map((row) => [
            row.key,
            countFacetMatches(funds, filters, row.key, row.options.map((option) => option.value), language, distributions),
        ])),
        // `rows` is rebuilt every render but only from these inputs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [funds, filters, language, distributions, typeOptions, activityOptions, currencyOptions, assetClassOptions],
    );

    return (
        <Stack gap="xs">
            {rows
                // A row with nothing to choose between (e.g. every fund is in one currency) is noise.
                .filter((row) => row.options.length > 1)
                .map((row) => (
                    <PillRow
                        key={row.key}
                        title={row.title}
                        allLabel={row.allLabel}
                        options={row.options}
                        selected={filters[row.key]}
                        allCount={counts[row.key].all}
                        counts={counts[row.key].byValue}
                        language={language}
                        onChange={(value) => onChange(row.key, value)}
                    />
                ))}
        </Stack>
    );
};

export default CatalogueFilterPills;
